import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowBackOutlined, DashboardOutlined, LockOutlined, SupportAgentOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Props {
  /** Optional heading override (defaults to "Access denied"). */
  title?: string;
  /** Optional body message override. */
  message?: string;
}

/**
 * Reusable Unauthorized / Access Denied screen (Phase 11.15). Rendered by every route-level PermissionRoute guard
 * and usable inline anywhere a section is permission-gated. Accessible (role="alert", aria-live) with Back /
 * Dashboard / Contact-administrator actions. Props are optional so existing `<Forbidden />` usages are unchanged.
 */
export default function Forbidden({ title = 'Access denied', message }: Props) {
  const navigate = useNavigate();
  const body = message
    ?? "You don't have permission to access this page. Contact your administrator if you believe this is a mistake.";

  const contactAdmin = () => {
    const subject = encodeURIComponent('GoERP Travel ERP — Access request');
    const bodyText = encodeURIComponent(`I need access to a restricted screen in GoERP Travel ERP.\nPage: ${window.location.pathname}`);
    window.location.href = `mailto:?subject=${subject}&body=${bodyText}`;
  };

  return (
    <Box role="alert" aria-live="assertive" sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', p: 2 }}>
      <Stack alignItems="center" spacing={1.5} textAlign="center" sx={{ width: '100%', maxWidth: 480 }}>
        <LockOutlined sx={{ fontSize: { xs: 48, sm: 56 }, color: 'warning.main' }} aria-hidden />
        <Typography variant="h5" component="h1">{title}</Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={440}>{body}</Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          flexWrap="wrap"
          justifyContent="center"
          useFlexGap
          sx={{ pt: 1, width: { xs: '100%', sm: 'auto' } }}
        >
          <Button variant="outlined" startIcon={<ArrowBackOutlined />} onClick={() => navigate(-1)}>Back</Button>
          <Button variant="contained" startIcon={<DashboardOutlined />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
          <Button variant="text" startIcon={<SupportAgentOutlined />} onClick={contactAdmin}>Contact administrator</Button>
        </Stack>
      </Stack>
    </Box>
  );
}
